import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, FormFeedback, Tooltip } from 'reactstrap';

const DimensionInput = ({
    label = "Enter Dimensions",
    required = false,
    unitOptions = ["Cm", "Mm", "M", "In", "Ft", "Yd"],
    defaultUnit = "Cm",
    helpText = "Please enter valid dimensions.",
    showHelp = false,
    minLength,
    maxLength,
    requiredErrorMessage = "Dimensions are required.",
    rangeErrorMessage = "Dimensions must be between [min] and [max] [unit].",
    invalidErrorMessage = "Please enter valid dimensions.",
    onSubmit,
    onChange,
    layout = "below",
    name,
    value = { length: '', width: '', height: '', unit: defaultUnit },
    validateTrigger = false,
}) => {
    const [dimensions, setDimensions] = useState({
        length: value.length,
        width: value.width,
        height: value.height,
        unit: value.unit || defaultUnit
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleDimensionChange = (dimensionType, dimensionValue) => {
        const updatedDimensions = {
          ...dimensions,
          [dimensionType]: dimensionValue
        };
        setDimensions(updatedDimensions);
        onChange(name, updatedDimensions); // Send updated dimensions directly
      };

    const validateDimensions = () => {
        const { length, width, height } = dimensions;
        const lengthNum = parseFloat(length);
        const widthNum = parseFloat(width);
        const heightNum = parseFloat(height);
        let error = '';

        if (required && (!length || !width || !height)) {
            error = requiredErrorMessage;
        } else if (
            (minLength && (lengthNum < minLength || widthNum < minLength || heightNum < minLength)) ||
            (maxLength && (lengthNum > maxLength || widthNum > maxLength || heightNum > maxLength))
        ) {
            error = rangeErrorMessage.replace("[min]", minLength).replace("[max]", maxLength).replace("[unit]", dimensions.unit);
        } else if (
            isNaN(lengthNum) || isNaN(widthNum) || isNaN(heightNum) || 
            lengthNum < 0 || widthNum < 0 || heightNum < 0
        ) {
            error = invalidErrorMessage;
        }

        setErrorMessage(error);
        return error === '';
    };

    useEffect(() => {
        if (validateTrigger) {
            validateDimensions();
        }
    }, [validateTrigger, dimensions]);

    // Use effect to parse the response dimensions correctly
    useEffect(() => {
        // Check if value is a stringified JSON object and parse it
        if (typeof value === 'string') {
            try {
                const parsedDimensions = JSON.parse(value);
                setDimensions(parsedDimensions);
            } catch (e) {
                console.error("Error parsing dimensions:", e);
            }
        } else if (value && value.length && value.width && value.height && value.unit) {
            // If it's already in the correct format, set the state directly
            setDimensions({
                length: value.length || '',
                width: value.width || '',
                height: value.height || '',
                unit: value.unit || defaultUnit
            });
        }
    }, [value, defaultUnit]);

    return (
        <>
            <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
                <Label style={{ display: 'flex', width:"20%", alignItems: 'center' }}>
                    {label} {required && <span style={{ color: 'red' }}>*</span>}
                    {showHelp && (
                        <span id="helpTooltip" className="px-2">
                            <i
                                className="ri-question-line"
                                style={{ cursor: 'pointer', fontSize: '16px' }}
                            ></i>
                        </span>
                    )}
                </Label>
                {showHelp && (
                    <Tooltip
                        placement="right"
                        isOpen={tooltipOpen}
                        target="helpTooltip"
                        toggle={() => setTooltipOpen(!tooltipOpen)}
                    >
                        {helpText}
                    </Tooltip>
                )}
                <div className={`input-group mb-1 w-100 ${layout === "inline" ? "d-flex align-items-center" : ""}`}>
                    <Input
                        type="number"
                        name={`${name}.length`}
                        value={dimensions.length}
                        onChange={(e) => handleDimensionChange('length', e.target.value)}
                        min="0"
                        placeholder="Length"
                        style={{ borderRight: 'none', borderRadius: '0.25rem 0 0 0.25rem' }}
                        invalid={!!errorMessage}
                    />
                    <div className="input-group-prepend">
                        <span className="input-group-text" style={{ background: "#fff", borderRadius: "0" }}>X</span>
                    </div>
                    <Input
                        type="number"
                        name={`${name}.width`}
                        value={dimensions.width}
                        onChange={(e) => handleDimensionChange('width', e.target.value)}
                        min="0"
                        placeholder="Width"
                        style={{ borderRight: 'none', borderLeft: 'none' }}
                        invalid={!!errorMessage}
                    />
                    <div className="input-group-prepend">
                        <span className="input-group-text" style={{ background: "#fff", borderRadius: "0" }}>X</span>
                    </div>
                    <Input
                        type="number"
                        name={`${name}.height`}
                        value={dimensions.height}
                        onChange={(e) => handleDimensionChange('height', e.target.value)}
                        min="0"
                        placeholder="Height"
                        style={{ borderLeft: 'none', borderRadius: '0 0.25rem 0.25rem 0' }}
                        invalid={!!errorMessage}
                    />
                    <div className="input-group-append">
                        <select
                            value={dimensions.unit}
                            onChange={(e) => handleDimensionChange('unit', e.target.value)}
                            className="form-select no-focus-outline"
                            style={{ flex: "1", backgroundColor: '#f0f0f0', borderRadius: '0 0.25rem 0.25rem 0' }}
                        >
                            {unitOptions.map((unit) => (
                                <option key={unit} value={unit}>
                                    {unit}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {errorMessage && <div style={{ color: '#f06548', fontSize: "0.875em" }}>{errorMessage}</div>}
            </div>
        </>
    );
};

export default DimensionInput;





















// import React, { useState, useEffect } from 'react';
// import { FormGroup, Label, Input, FormFeedback, Tooltip } from 'reactstrap';

// const DimensionInput = ({
//     label = "Enter Dimensions",
//     required = false,
//     unitOptions = ["cm", "mm", "m", "in", "ft", "yd"],
//     defaultUnit = "cm",
//     helpText = "Please enter valid dimensions.",
//     showHelp = false,
//     minLength,
//     maxLength,
//     requiredErrorMessage = "Dimensions are required.",
//     rangeErrorMessage = "Dimensions must be between [min] and [max] [unit].",
//     invalidErrorMessage = "Please enter valid dimensions.",
//     onSubmit,
//     onChange,
//     layout = "below",
//     name,
//     value = { length: '', width: '', height: '', unit: defaultUnit },
//     validateTrigger = false,
// }) => {
//     const [dimensions, setDimensions] = useState({
//         length: value.length,
//         width: value.width,
//         height: value.height,
//         unit: value.unit || defaultUnit
//     });
//     const [errorMessage, setErrorMessage] = useState('');
//     const [tooltipOpen, setTooltipOpen] = useState(false);

//     const handleDimensionChange = (dimensionType, dimensionValue) => {
//         const updatedDimensions = {
//           ...dimensions,
//           [dimensionType]: dimensionValue
//         };
//         setDimensions(updatedDimensions);
//         onChange(name, updatedDimensions); // Send updated dimensions directly
//       };
      

//     const validateDimensions = () => {
//         const { length, width, height } = dimensions;
//         const lengthNum = parseFloat(length);
//         const widthNum = parseFloat(width);
//         const heightNum = parseFloat(height);
//         let error = '';

//         if (required && (!length || !width || !height)) {
//             error = requiredErrorMessage;
//         } else if (
//             (minLength && (lengthNum < minLength || widthNum < minLength || heightNum < minLength)) ||
//             (maxLength && (lengthNum > maxLength || widthNum > maxLength || heightNum > maxLength))
//         ) {
//             error = rangeErrorMessage.replace("[min]", minLength).replace("[max]", maxLength).replace("[unit]", dimensions.unit);
//         } else if (
//             isNaN(lengthNum) || isNaN(widthNum) || isNaN(heightNum) || 
//             lengthNum < 0 || widthNum < 0 || heightNum < 0
//         ) {
//             error = invalidErrorMessage;
//         }

//         setErrorMessage(error);
//         return error === '';
//     };

//     useEffect(() => {
//         if (validateTrigger) {
//             validateDimensions();
//         }
//     }, [validateTrigger, dimensions]);

//     // Use effect to parse the response dimensions correctly
//     useEffect(() => {
//         if (value && value.length && value.width && value.height && value.unit) {
//             setDimensions({
//                 length: value.length || '',
//                 width: value.width || '',
//                 height: value.height || '',
//                 unit: value.unit || defaultUnit
//             });
//         }
//     }, [value, defaultUnit]);

//     return (
//         <>
//             <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
//                 <Label style={{ display: 'flex', alignItems: 'center' }}>
//                     {label} {required && <span style={{ color: 'red' }}>*</span>}
//                     {showHelp && (
//                         <span id="helpTooltip" className="px-2">
//                             <i
//                                 className="ri-question-line"
//                                 style={{ cursor: 'pointer', fontSize: '16px' }}
//                             ></i>
//                         </span>
//                     )}
//                 </Label>
//                 {showHelp && (
//                     <Tooltip
//                         placement="right"
//                         isOpen={tooltipOpen}
//                         target="helpTooltip"
//                         toggle={() => setTooltipOpen(!tooltipOpen)}
//                     >
//                         {helpText}
//                     </Tooltip>
//                 )}
//                 <div className={`input-group mb-1 ${layout === "inline" ? "d-flex align-items-center" : ""}`}>
//                     <Input
//                         type="number"
//                         name={`${name}.length`}
//                         value={dimensions.length}
//                         onChange={(e) => handleDimensionChange('length', e.target.value)}
//                         min="0"
//                         placeholder="Length"
//                         style={{ borderRight: 'none', borderRadius: '0.25rem 0 0 0.25rem' }}
//                         invalid={!!errorMessage}
//                     />
//                     <div className="input-group-prepend">
//                         <span className="input-group-text" style={{ background: "#fff", borderRadius: "0" }}>X</span>
//                     </div>
//                     <Input
//                         type="number"
//                         name={`${name}.width`}
//                         value={dimensions.width}
//                         onChange={(e) => handleDimensionChange('width', e.target.value)}
//                         min="0"
//                         placeholder="Width"
//                         style={{ borderRight: 'none', borderLeft: 'none' }}
//                         invalid={!!errorMessage}
//                     />
//                     <div className="input-group-prepend">
//                         <span className="input-group-text" style={{ background: "#fff", borderRadius: "0" }}>X</span>
//                     </div>
//                     <Input
//                         type="number"
//                         name={`${name}.height`}
//                         value={dimensions.height}
//                         onChange={(e) => handleDimensionChange('height', e.target.value)}
//                         min="0"
//                         placeholder="Height"
//                         style={{ borderLeft: 'none', borderRadius: '0 0.25rem 0.25rem 0' }}
//                         invalid={!!errorMessage}
//                     />
//                     <div className="input-group-append">
//                         <select
//                             value={dimensions.unit}
//                             onChange={(e) => handleDimensionChange('unit', e.target.value)}
//                             className="form-select"
//                             style={{ flex: "1", backgroundColor: '#f0f0f0', borderRadius: '0 0.25rem 0.25rem 0' }}
//                         >
//                             {unitOptions.map((unit) => (
//                                 <option key={unit} value={unit}>
//                                     {unit}
//                                 </option>
//                             ))}
//                         </select>
//                     </div>
//                 </div>
//                 {errorMessage && <div style={{ color: '#f06548', fontSize: "0.875em" }}>{errorMessage}</div>}
//             </div>
//         </>
//     );
// };

// export default DimensionInput;




